import type { IDropDownItem } from "@similarweb/ui-components";
import { Badge, Dropdown } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import {
    StyledDropdownParent,
    StyledSingleSelectDDItem,
    StyledSingleSelectDDItemText,
} from "app/pages/sales-intelligence/common-components/dropdown/styles";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { buildDropdownSelectedItemsFromArray } from "app/pages/sales-intelligence/utils/dropdowns";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import FilterButtonLabel from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import { StyledItemsDivider } from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import { NEW_TRAFFIC_SOURCE_TYPE_METRICS } from "app/pages/sales-intelligence/sub-modules/signals/constants/traffic/trafficMetrics";
import trafficSignalFiltersService from "app/pages/sales-intelligence/sub-modules/signals/services/ui-signals/traffic/trafficSignalFiltersService";
import type { ReactElement } from "react";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";

const filterI18nKeyPrefix = "si.signals.filters.traffic-metric";

type FilterValue = string;

type DropdownItem = Omit<IDropDownItem, "id"> & {
    id: FilterValue;
};

type SignalTrafficChannelsFilterProps = {
    selectedValue: FilterValue | undefined;
    options: readonly FilterValue[];
    autoOpen?: boolean;
    onValueChange(value: FilterValue): void;
};

const SignalTrafficChannelsFilter = (props: SignalTrafficChannelsFilterProps) => {
    const { selectedValue, options, autoOpen = false, onValueChange } = props;

    const selectedItems: Record<FilterValue, boolean> = buildDropdownSelectedItemsFromArray(
        selectedValue ? [selectedValue] : [],
    );

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, autoOpen);

    const isOptionLastOfType = trafficSignalFiltersService.checkIsTrafficMetricLastOfType(options);

    const handleChange = (item: DropdownItem): void => {
        onValueChange(item.id);
    };

    const renderOptions = (options: readonly FilterValue[]): readonly ReactElement[] => {
        const items: ReactElement[] = [];

        options.forEach((option, index) => {
            items.push(
                <StyledSingleSelectDDItem
                    key={option}
                    id={option}
                >
                    <I18n
                        data-automation="traffic-visits-dd-item-text"
                        element={StyledSingleSelectDDItemText}
                        translatable={{
                            key: `${filterI18nKeyPrefix}.${option}.name`,
                        }}
                    />
                    {(NEW_TRAFFIC_SOURCE_TYPE_METRICS as ReadonlySet<string>).has(option) && (
                        <Badge type="new" />
                    )}
                </StyledSingleSelectDDItem>,
            );

            if (isOptionLastOfType(option, index)) {
                items.push(<StyledItemsDivider />);
            }
        });

        return items;
    };

    return (
        <StyledDropdownParent id="traction-changes-dd">
            <Dropdown
                ref={dropdownRef}
                appendTo="#traction-changes-dd"
                selectedIds={selectedItems}
                width={200}
                dropdownPopupHeight={230}
                onClick={handleChange}
            >
                <SignalFilterDropdownButton id="traffic-visits-dd-button">
                    <FilterButtonLabel>{translate(`${filterI18nKeyPrefix}.${selectedValue}.name`)}</FilterButtonLabel>
                </SignalFilterDropdownButton>

                {...renderOptions(options)}
            </Dropdown>
        </StyledDropdownParent>
    );
};

export default SignalTrafficChannelsFilter;
